import { useEffect, useState, type KeyboardEvent } from "react";
import { Button, Dialog, Icon, Input, SortableCell, SortableGroups, type IconName, type SortableGroupRows } from "@omnabase/ui";
import { SIDEBAR_LIMITS, SIDEBAR_VIEWS, uuidv7, type AnySidebar, type Tag } from "@omnabase/shared";
import type { SidebarStore } from "../../lib/sidebar";
import { addGroup, addItem, builtInName, moveEntry, moveGroupTo, poolChoices, removeGroup, removeItem, renameGroup, setGroupHidden, setItemHidden, type EntryRef, type PoolChoice } from "../../lib/sidebarEdits";

/** "Customize the sidebar" (the sidebar and consent round, 2026-10-24, brief rule "The dialog"): the person's own inbox column, group by group.
 *  The sidebar editor rework (2026-10-26, docs/briefs/sidebar-rework-round.md): one drag for everything (the kit's nested list): groups move
 *  among themselves, entries within their group and into another one (dropped on a group's heading an entry joins it at its end); every group
 *  has its name as a field, the kit's checkbox "Show" and Remove; every entry its checkbox "Show" and Remove; the "+ Add" pool per group with a
 *  full-width search. No Fold switch (folding happens in the column), no "Move to", no up/down. No confirmation before a removal: the column
 *  previews it live and "Reset to default" is the way back. The column follows every change at once; the store saves a moment later; Done or
 *  Escape sends what waits and closes.
 *  The customers sidebar round (2026-10-29, brief rule 2 "the inbox's code, not a copy"; report finding 9): one dialog for both columns. The
 *  column (`area`) brings its words, how an entry looks and what its pool offers (`inboxArea` here, the Customers column's in
 *  `screens/customers/customersViews.ts`); the drag, the folding and the removals are this file's for both. */
export type ColumnArea = {
  title: string; intro: string; search: string;
  /** an entry's words and mark */
  look: (item: EntryRef) => { label: string; icon: IconName };
  /** what the pool offers, given the layout and the search */
  pool: (layout: AnySidebar, search: string) => PoolChoice[];
  /** the quiet kind marker beside a pool choice ("Customer tag"), or nothing */
  kindLabel: (kind: string) => string | null;
};
/** the inbox's column: views and both kinds of tag */
export function inboxArea(tags: readonly Tag[]): ColumnArea {
  return {
    title: "Customize the sidebar", intro: "Arrange the inbox's left column for yourself: show, hide, sort, group and remove its views and tags.", search: "Search views and tags",
    look: (item) => entryLook(item, tags), pool: (layout, search) => poolChoices(layout, tags, search), kindLabel: (kind) => (kind === "customerTag" ? "Customer tag" : null),
  };
}
export function CustomizeSidebar<L extends AnySidebar>({ store, area, onClose }: { store: SidebarStore<L>; area: ColumnArea; onClose: () => void }) {
  const { layout } = store;
  const change = (next: AnySidebar) => store.change(next as L); // every edit answers a layout of the column it was given
  useBackgroundHidden();
  const close = () => { store.flush(); onClose(); };
  const footer = <>
    <Button icon="plus" onClick={() => change(addGroup(layout, `g-${uuidv7()}`))}>New group</Button>
    <Button variant="ghost" icon="refresh" onClick={store.reset}>Reset to default</Button>
    <span className="ob-sidebar-edit__spacer" />
    <Button variant="primary" onClick={close}>Done</Button>
  </>;
  return (
    <Dialog title={area.title} intro={area.intro} footer={footer} onClose={close} width={600}>
      <GroupList layout={layout} area={area} change={change} />
    </Dialog>
  );
}

/** while the dialog is open the page behind it is hidden from assistive technology (a modal's background), apart from the views column, which
 *  previews every change live */
function useBackgroundHidden() {
  useEffect(() => {
    const behind = [...document.querySelectorAll<HTMLElement>(".ob-rail, .ob-tabbar, .ob-topbar, .ob-body > :not(.ob-views)")].filter((el) => !el.hasAttribute("aria-hidden"));
    for (const el of behind) el.setAttribute("aria-hidden", "true");
    return () => { for (const el of behind) el.removeAttribute("aria-hidden"); };
  }, []);
}

type Group = { id: string; name: string; builtIn?: string; hidden: boolean; folded: boolean; items: { kind: string; id: string; hidden: boolean }[] };
type Edit = { layout: AnySidebar; area: ColumnArea; change: (next: AnySidebar) => void };
const groupsOf = (l: AnySidebar) => l.groups as Group[];
type Drag = { drag: SortableGroupRows };
/** an entry's key inside the drag: its kind and id (unique in a layout) */
const keyOf = (i: EntryRef) => `${i.kind}:${i.id}`;
function refOf(key: string): EntryRef { const at = key.indexOf(":"); return { kind: key.slice(0, at), id: key.slice(at + 1) }; }
/** the name a group goes by while its field is blank (what the save will call it) */
const nameOf = (g: Group) => g.name.trim() || (g.builtIn ? builtInName(g.builtIn) : "New group");

/** the groups in their order, in one drag with their entries */
function GroupList({ layout, area, change }: Edit) {
  const model = groupsOf(layout).map((g) => ({ id: g.id, label: nameOf(g), items: g.items.map(keyOf) }));
  return (
    <SortableGroups groups={model} onMoveGroup={(id, to) => change(moveGroupTo(layout, id, to))} onMoveEntry={(key, toGroup, to) => change(moveEntry(layout, refOf(key), toGroup, to))}>
      {(drag) => (
        <div className="ob-sidebar-edit">
          {groupsOf(layout).map((g) => <GroupSection key={g.id} group={g} drag={drag} layout={layout} area={area} change={change} />)}
        </div>
      )}
    </SortableGroups>
  );
}

/** one group: its heading (handle, name field, Show, Remove), its entries and the pool */
function GroupSection({ group: g, drag, layout, area, change }: Edit & Drag & { group: Group }) {
  const [adding, setAdding] = useState(false);
  const name = nameOf(g);
  return (
    <section {...drag.group(g.id, "ob-sidebar-edit__group")} role="group" aria-label={name}>
      <div {...drag.head()} className="ob-sidebar-edit__head">
        {drag.groupHandle(g.id, `group ${name}`)}
        <Input className="ob-input--text ob-sidebar-edit__name-field" aria-label="Group name" value={g.name} maxLength={SIDEBAR_LIMITS.nameMax} onChange={(e) => change(renameGroup(layout, g.id, e.target.value))} />
        <label className="ob-sidebar-edit__toggle"><input type="checkbox" className="ob-check" aria-label={`Show ${name}`} checked={!g.hidden} onChange={(e) => change(setGroupHidden(layout, g.id, !e.target.checked))} />Show</label>
        <Button variant="icon" size="sm" icon="trash" aria-label={`Remove group ${name}`} onClick={() => change(removeGroup(layout, g.id))} />
      </div>
      <EntryTable group={g} drag={drag} layout={layout} area={area} change={change} />
      {adding ? <Pool group={g} layout={layout} area={area} change={change} onClose={() => setAdding(false)} />
        : <Button variant="ghost" size="sm" icon="plus" className="ob-sidebar-edit__add" aria-label={`Add to ${name}`} onClick={() => setAdding(true)}>Add</Button>}
    </section>
  );
}

/** an entry's words and mark: a view's label, a tag's name (a customer tag with the person icon) */
function entryLook(item: EntryRef, tags: readonly Tag[]): { label: string; icon: IconName } {
  if (item.kind === "view") return { label: SIDEBAR_VIEWS[item.id]?.label ?? item.id, icon: (SIDEBAR_VIEWS[item.id]?.icon ?? "inbox") as IconName };
  return { label: tags.find((t) => t.id === item.id)?.name ?? "Unknown tag", icon: item.kind === "customerTag" ? "user" : "tag" };
}

/** the group's entries as rows: the handle, Show, the name with the kind's icon, Remove */
function EntryTable({ group: g, drag, layout, area, change }: Edit & Drag & { group: Group }) {
  if (!g.items.length) return <p className="ob-sidebar-edit__empty">No entries yet.</p>;
  return (
    <table className="ob-sidebar-edit__entries">
      <tbody>
        {g.items.map((item) => {
          const { label, icon } = area.look(item);
          return (
            <tr key={keyOf(item)} {...drag.entry(keyOf(item), "ob-sidebar-edit__entry")}>
              <SortableCell>{drag.entryHandle(keyOf(item), label)}</SortableCell>
              <td className="ob-sidebar-edit__show"><input type="checkbox" className="ob-check" aria-label={`Show ${label}`} checked={!item.hidden} onChange={(e) => change(setItemHidden(layout, item, !e.target.checked))} /></td>
              <td className="ob-sidebar-edit__label"><Icon name={icon} size="sm" /><span>{label}</span></td>
              <td className="ob-sidebar-edit__tools"><Button variant="icon" size="sm" icon="x" aria-label={`Remove ${label}`} onClick={() => change(removeItem(layout, item))} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** the pool: what the group can take, searchable by name (the search full width, Close on its own line under the choices); a pick adds the
 *  entry and closes the pool; Escape closes it alone */
function Pool({ group: g, layout, area, change, onClose }: Edit & { group: Group; onClose: () => void }) {
  const [search, setSearch] = useState("");
  const choices = area.pool(layout, search);
  const pick = (c: PoolChoice) => { change(addItem(layout, g.id, c)); onClose(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
  return (
    <div className="ob-sidebar-edit__pool" onKeyDown={onKey}>
      <Input className="ob-input--text ob-sidebar-edit__search" aria-label="Search" placeholder={area.search} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
      {choices.length ? (
        <ul className="ob-sidebar-edit__choices" role="listbox" aria-label={`Add to ${nameOf(g)}`}>
          {choices.map((c) => (
            <li key={`${c.kind}:${c.id}`} role="option" aria-selected={false} tabIndex={0} className="ob-sidebar-edit__choice" onClick={() => pick(c)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(c); } }}>
              <Icon name={c.icon as IconName} size="sm" /><span>{c.label}</span>{area.kindLabel(c.kind) && <span className="ob-sidebar-edit__kind">{area.kindLabel(c.kind)}</span>}
            </li>
          ))}
        </ul>
      ) : <p className="ob-sidebar-edit__empty">Nothing left to add{search ? " for this search" : ""}.</p>}
      <div className="ob-sidebar-edit__pool-foot"><Button variant="ghost" size="sm" onClick={onClose}>Close</Button></div>
    </div>
  );
}
